import { Controller } from 'stimulus';
import Rails from '@rails/ujs';

export default class extends Controller {
  static targets = [
    'subjectInput',
    'bodyInput',
    'recipientTable',
    'recipientCheckbox',
    'individualRecipientCheckbox',
    'recipientCheckbox',
    'conditionsSection',
    'scheduleTypeInput'
  ];

  connect() {
    requestAnimationFrame(() => {

      if (this._isAnyGroupRecipientSelected()) {
        this._disableIndividualRecipients();
        this._toggleRequiredGroupRecipients(false);
        this._toggleConditionsSection(true);
      }

      if (this.hasIndividualRecipientCheckboxTarget) {
        if (this.individualRecipientCheckboxTarget.checked) {
          this._disableGroupRecipients();
          this._toggleRequiredGroupRecipients(false);
          this._toggleConditionsSection(false);
        }
      }
    });
  }

  changeTemplateContent(event) {
    const templateUrl = event.currentTarget.dataset["components-AdminDropdownsDropdownUrlParam"];

    fetch(templateUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => this._replaceContentTargets(data))
  }

  _replaceContentTargets(template) {
    this.subjectInputTarget.value = template.subject;
    this.bodyInputTarget.value = template.body;

    // Dispatch input events to notify form validation controller
    this.subjectInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.bodyInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  toggleRecipientTable(event) {
    const isIndividualRecipient = event.target === this.individualRecipientCheckboxTarget;
    const isAnyCheckboxChecked = isIndividualRecipient ? event.target.checked : this._isAnyGroupRecipientSelected();

    if (isAnyCheckboxChecked) {
      if (isIndividualRecipient) {
        // When individual recipient is selected
        this._disableGroupRecipients();
        this._toggleRequiredGroupRecipients(false);
        this._toggleConditionsSection(false);
        this._openRecipientTable();
      } else {
        // When group recipients are selected
        this._disableIndividualRecipients();
        this._toggleConditionsSection(true);
        const selectedRoles = this._getSelectedGroupRoles();
        this._openPreviewRecipientsTable(selectedRoles);
      }
    } else {
      if (isIndividualRecipient) { // When individual recipient is deselected
        this._enableGroupRecipients();
        this._toggleConditionsSection(true);
      } else {
        this._enableIndividualRecipients();
      }
      this._closeRecipientTable();
    }
  }

  toggleGroupRecipient() {
    if (this._isAnyGroupRecipientSelected()) {
      this._disableIndividualRecipients();
      this._toggleRequiredGroupRecipients(false);
    } else {
      this._enableIndividualRecipients();
      this._toggleRequiredGroupRecipients(true);
    }
  }

  updateTableOnFilterChange() {
    if (!this.recipientTableTarget.classList.contains('hidden') && this._isAnyGroupRecipientSelected()) {
      const selectedTypes = this._getSelectedGroupRoles();
      this._openPreviewRecipientsTable(selectedTypes);
    }
  }

  _openPreviewRecipientsTable(roles) {
    this.recipientTableTarget.classList.remove('hidden');

    const url = this._buildMembersUrl(roles);

    Rails.ajax({
      url: url,
      type: "GET",
      dataType: "script",
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      }
    })
  }

  _openRecipientTable() {
    this.recipientTableTarget.classList.remove('hidden');

    Rails.ajax({
      url: this.membersUrl,
      type: "GET",
      dataType: "script",
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      },
      success: () => {
        requestAnimationFrame(() => {
          const searchInput = this.recipientTableTarget.querySelector(
            '[data-components--admin-data-table-target="searchInput"]'
          );
          if (searchInput) {
            searchInput.focus();
          }
        });
      }
    })
  }

  _closeRecipientTable() {
    this.recipientTableTarget.classList.add('hidden');
    this.recipientTableTarget.innerHTML = '';
  }

  _toggleConditionsSection(show) {
    if (show) {
      this.conditionsSectionTarget.classList.remove('hidden');
    } else {
      this.conditionsSectionTarget.classList.add('hidden');
    }
  }

  _disableGroupRecipients() {
    if (this.hasRecipientCheckboxTarget) {
      this.recipientCheckboxTargets.forEach(checkbox => {
        checkbox.checked = false;
        checkbox.disabled = true;
        checkbox.required = false;

        checkbox.style.cursor = 'not-allowed';
        checkbox.closest('label')?.style.setProperty('cursor', 'not-allowed');
      });
    }
  }

  _enableGroupRecipients() {
    if (this.hasRecipientCheckboxTarget) {
      this.recipientCheckboxTargets.forEach(checkbox => {
        checkbox.disabled = false;
        checkbox.required = true;

        checkbox.style.cursor = 'pointer';
        checkbox.closest('label')?.style.setProperty('cursor', '');
      });
    }
  }

  _toggleRequiredGroupRecipients(required) {
    if (this.hasRecipientCheckboxTarget) {
      this.recipientCheckboxTargets.forEach(checkbox => {
        checkbox.required = required;
      });
    }
  }

  _disableIndividualRecipients() {
    if (this.hasIndividualRecipientCheckboxTarget) {
      this.individualRecipientCheckboxTarget.checked = false;
      this.individualRecipientCheckboxTarget.disabled = true;

      this.individualRecipientCheckboxTarget.style.cursor = 'not-allowed';
      this.individualRecipientCheckboxTarget.closest('label')?.style.setProperty(
        'cursor', 'not-allowed'
      );
    }
  }

  _enableIndividualRecipients() {
    if (this.hasIndividualRecipientCheckboxTarget) {
      this.individualRecipientCheckboxTarget.disabled = false;

      this.individualRecipientCheckboxTarget.style.cursor = 'pointer';
      this.individualRecipientCheckboxTarget.closest('label')?.style.setProperty('cursor', '');
    }
  }

  _isAnyGroupRecipientSelected() {
    if (!this.hasRecipientCheckboxTarget) return false;

    return this.recipientCheckboxTargets.some(checkbox => checkbox.checked);
  }

  _getSelectedGroupRoles() {
    if (!this.hasRecipientCheckboxTarget) return [];

    const roles = [];
    this.recipientCheckboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        const membershipRole = checkbox.dataset.membershipRole;
        if (membershipRole) {
          roles.push(membershipRole);
        }
      }
    });
    return roles;
  }

  _buildMembersUrl(roles) {
    let url = this.membersUrl;
    const params = [];

    // filter by recipient roles
    if (roles && roles.length > 0) {
      roles.forEach(role => params.push(`roles[]=${encodeURIComponent(role)}`));

      // filter by conditions (only in preview mode)
      const filterRadio = document.querySelector('input[name="schedule_config[filter]"]:checked');
      const selectedFilter = filterRadio ? filterRadio.value : null;

      if (selectedFilter) {
        params.push(`recipient_condition=${encodeURIComponent(selectedFilter)}`);
      }

      if (this._isSendImmediately()) {
        params.push('send_immediately=true');
      }
    }

    if (params.length > 0) {
      url += (url.includes('?') ? '&' : '?') + params.join('&');
    }

    return url;
  }

  _isSendImmediately() {
    return this.hasScheduleTypeInputTarget;
  }

  get membersUrl() {
    return this.data.get('recipientTableUrl');
  }
}
